<template>
  <div
    v-if="hasActivePromotion"
    class="promotion-sidebar-button"
    @click="openShop"
  >
    <div class="button-content">
      <div class="icon-container">
        <i class="material-icons">local_fire_department</i>
      </div>

      <div class="promo-info">
        <span class="promo-label">{{ $t('promotions.sidebar.label') }}</span>
        <span class="promo-bonus">+{{ highestBonus }}%</span>
      </div>
    </div>

    <div class="pulse-effect"></div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'PromotionSidebarButton',

  data() {
    return {
      intervalId: null
    };
  },

  computed: {
    ...mapState('monetization', ['sidebarPromotions']),

    hasActivePromotion() {
      return this.sidebarPromotions && this.sidebarPromotions.length > 0;
    },

    highestBonus() {
      if (!this.hasActivePromotion) return 0;

      return Math.max(
        ...this.sidebarPromotions.map(promo => promo.bonusPercentage)
      );
    }
  },

  async mounted() {
    await this.loadPromotions();

    // Refresh promotions every 5 minutes
    this.intervalId = setInterval(this.loadPromotions, 5 * 60 * 1000);
  },

  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },

  methods: {
    ...mapActions('monetization', ['fetchSidebarPromotions']),

    async loadPromotions() {
      try {
        await this.fetchSidebarPromotions();
      } catch (error) {
        console.error('Error loading sidebar promotions:', error);
      }
    },

    openShop() {
      this.$emit('open-shop');
    }
  }
};
</script>

<style scoped lang="scss">
$primary-red: #ff4646;
$primary-blue: #3498db;
$dark-bg: #1a1a1a;
$gold: #ffd700;

.promotion-sidebar-button {
  position: fixed;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, $primary-red, #ff1a1a);
  border: 2px solid $primary-red;
  border-radius: 25px;
  padding: 8px 16px;
  cursor: pointer;
  z-index: 9999;
  box-shadow: 0 0 20px rgba($primary-red, 0.6);
  transition: all 0.3s ease;
  animation: slideInRight 0.5s ease;
  min-width: 120px;
  max-width: 160px;

  &:hover {
    transform: translateY(-50%) translateX(-3px) scale(1.02);
    box-shadow: 0 0 25px rgba($primary-red, 0.8);
  }

  .button-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    position: relative;
    z-index: 1;

    .icon-container {
      background: rgba(white, 0.2);
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: pulse 2s infinite;
      flex-shrink: 0;

      .material-icons {
        font-size: 18px;
        color: white;
        filter: drop-shadow(0 0 8px rgba(white, 0.8));
      }
    }

    .promo-info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      overflow: hidden;

      .promo-label {
        font-family: 'Orbitron', sans-serif;
        font-size: 0.65rem;
        font-weight: bold;
        color: white;
        letter-spacing: 0.5px;
        text-shadow: 0 0 8px rgba(white, 0.8);
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 80px;
      }

      .promo-bonus {
        font-size: 1.1rem;
        font-weight: bold;
        color: $gold;
        text-shadow: 0 0 10px rgba($gold, 0.8);
        line-height: 1;
      }
    }
  }

  .pulse-effect {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    border: 2px solid $primary-red;
    border-radius: 25px;
    animation: pulseEffect 2s infinite;
    pointer-events: none;
  }
}

// Animations
@keyframes slideInRight {
  from {
    transform: translateY(-50%) translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateY(-50%) translateX(0);
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes pulseEffect {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

// Responsive
@media (max-width: 1024px) {
  .promotion-sidebar-button {
    right: 10px;
    padding: 6px 12px;
    min-width: 100px;
    max-width: 140px;

    .button-content {
      gap: 6px;

      .icon-container {
        width: 28px;
        height: 28px;

        .material-icons {
          font-size: 16px;
        }
      }

      .promo-info {
        .promo-label {
          font-size: 0.6rem;
          max-width: 70px;
        }

        .promo-bonus {
          font-size: 1rem;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .promotion-sidebar-button {
    right: 8px;
    padding: 5px 10px;
    min-width: 80px;
    max-width: 110px;
    top: 60%;

    .button-content {
      gap: 5px;

      .icon-container {
        width: 24px;
        height: 24px;

        .material-icons {
          font-size: 14px;
        }
      }

      .promo-info {
        .promo-label {
          font-size: 0.55rem;
          max-width: 60px;
        }

        .promo-bonus {
          font-size: 0.9rem;
        }
      }
    }
  }
}

// Ultra small mobile
@media (max-width: 480px) {
  .promotion-sidebar-button {
    right: 5px;
    padding: 4px 8px;
    min-width: 70px;
    max-width: 90px;
    top: 65%;

    .button-content {
      gap: 4px;

      .icon-container {
        width: 20px;
        height: 20px;

        .material-icons {
          font-size: 12px;
        }
      }

      .promo-info {
        .promo-label {
          font-size: 0.5rem;
          max-width: 50px;
        }

        .promo-bonus {
          font-size: 0.8rem;
        }
      }
    }
  }
}
</style>
